/**
 * 버튼에 붙어 뜨는 판의 화면 좌표(74차 UXV74-08). `MenuButton` 의 메뉴 판과 `InfoTip` 의 설명 판이 같이 쓴다.
 * - 좌표는 화면(뷰포트) 기준 — 판은 `position: fixed`(또는 top layer)라 표 스크롤 래퍼 · 카드 overflow 에 잘리지 않는다.
 * - 버튼 아래에 붙이고, 아래 공간이 모자라고 위에 들어가면 위로 뒤집는다. 버튼이 스크롤로 화면 밖에 나가도 판은 화면 안(가장자리
 *   `edge` 안쪽)에 남긴다 — 상단바 위 · 음수 좌표 금지.
 * - 판의 크기는 지금 그려진 크기(offsetWidth · offsetHeight)로 잰다. `minWidth` 를 새로 정하면 폭이 바뀌므로, 호출부는 그 값을
 *   적용한 뒤 한 번 더 부른다(MenuButton 이 두 번 부르는 이유).
 * - 75차(UXS75-18 ③): 세로 공간은 `window.visualViewport`(있으면)로 잰다 — iOS 에서 키보드가 떠도 `innerHeight` 는 줄지 않아
 *   입력칸 근처 판이 키보드 밑에 열렸다. 없으면 `innerHeight`.
 * - `anchorOutOfView`(75차 UXS75-05 ②): 설명 판(InfoTip · AiTrace)은 버튼이 보이는 띠 밖으로 나가면 닫는다 — 가장자리에 붙어 대상
 *   없이 떠 있게 된다. 메뉴(MenuButton)는 지금처럼 화면 안에 남긴다. 띠는 **실제로 가리는 것만** 뺀다(fix1 OQ-1 — 처음엔 늘
 *   `--ui-topbar-h` 56px 를 위 띠로 봐서, 상단바가 없는 화면(공개 셸 · 로그인)이나 화면 맨 위 대화상자 머리의 ⓘ 는 열자마자 닫혔다):
 *   ① 상단바(`header.topbar`)는 그려져 있고 버튼이 대화상자(`dialog` · `role=dialog` · `aria-modal` · popover) · 상단바 밖일 때만 그 아래 끝을
 *   잰 값으로(≤1180 에서 static 이라 스크롤로 지나가면 0) ② 버튼을 품은 스크롤 · 잘림 상자(시트 본문 · 모달 본문 · 표 래퍼)의 테두리.
 * - 차트 툴팁(`charts/tooltip.ts`)은 차트 상자 안 좌표라 이 도우미를 쓰지 않는다. 떠 있는 판이 세 종류를 넘거나 스크롤 상자 안
 *   추적이 필요해지면 `@floating-ui/dom` 으로 바꾼다(ux-viz §4).
 */

export interface AnchorPlacement {
  /** 판 위 y(px, 화면 기준) */
  top: number;
  /** 판 왼쪽 x(px, 화면 기준) */
  left: number;
  /** 판 최소 폭(px) — 버튼 폭과 `minWidth` 중 큰 값 */
  minWidth: number;
  /** 버튼 위로 뒤집었다 */
  flipped: boolean;
}

export interface AnchorOptions {
  /** 버튼 왼쪽 끝(start) · 오른쪽 끝(end)에 맞춘다. 기본 bottom-start */
  placement?: "bottom-start" | "bottom-end";
  /** 버튼과 판 사이(px). 기본 4 */
  gap?: number;
  /** 화면 가장자리 여백(px). 기본 8 */
  edge?: number;
  /** 판 최소 폭(px). 기본 0 — 버튼 폭보다 좁아지지 않는다 */
  minWidth?: number;
}

/** 보이는 화면의 세로 범위(레이아웃 뷰포트 좌표) — 키보드가 가린 아래는 뺀다 */
function visibleRange(): { top: number; bottom: number } {
  const view = window.visualViewport;
  if (!view) return { top: 0, bottom: window.innerHeight };
  return { top: view.offsetTop, bottom: view.offsetTop + view.height };
}

/** 버튼이 대화상자 층 안이다 — 상단바 위에 떠 있어 상단바가 가리지 않는다 */
const DIALOG_LAYER = 'dialog, [role="dialog"], [aria-modal="true"], [popover]';
/** 내용을 잘라 보이는 상자(스크롤 · 잘림) */
const CLIPPING = /^(auto|scroll|hidden|clip)$/;

/** 버튼이 실제로 보이는 띠 밖으로 완전히 나갔다(설명 판을 닫을 때) */
export function anchorOutOfView(trigger: HTMLElement): boolean {
  const rect = trigger.getBoundingClientRect();
  let { top, bottom } = visibleRange();
  if (!trigger.closest(DIALOG_LAYER)) {
    const bar = document.querySelector<HTMLElement>("header.topbar");
    const barRect = bar?.contains(trigger)
      ? undefined
      : bar?.getBoundingClientRect();
    if (barRect && barRect.height > 0) top = Math.max(top, barRect.bottom);
  }
  for (
    let el = trigger.parentElement;
    el && el !== document.body && el !== document.documentElement;
    el = el.parentElement
  ) {
    if (!CLIPPING.test(getComputedStyle(el).overflowY)) continue;
    const box = el.getBoundingClientRect();
    top = Math.max(top, box.top);
    bottom = Math.min(bottom, box.bottom);
  }
  return rect.bottom <= top || rect.top >= bottom;
}

/** 버튼 아래(모자라면 위)에 판을 붙일 화면 좌표 */
export function placeAnchored(
  trigger: HTMLElement,
  panel: HTMLElement,
  options?: AnchorOptions,
): AnchorPlacement {
  const gap = options?.gap ?? 4;
  const edge = options?.edge ?? 8;
  const rect = trigger.getBoundingClientRect();
  const viewportWidth = document.documentElement.clientWidth;
  const { top: viewTop, bottom: viewBottom } = visibleRange();
  const height = panel.offsetHeight;
  // 최소 폭(`minWidth` · 버튼 폭)은 호출부가 판 style 에 넣는 값이라 같은 틱에는 아직 적용 전일 수 있다(MenuButton 이 연달아 두 번 부른다 —
  // 첫 계산의 minWidth 는 다음 렌더에야 DOM 에 닿는다). 그 전의 좁은 offsetWidth 로 오른쪽을 맞추면 판이 화면 밖으로 삐져나가므로 최소 폭을 미리 반영한다(81차).
  const width = Math.max(panel.offsetWidth, options?.minWidth ?? 0, rect.width);

  let top = rect.bottom + gap;
  let flipped = false;
  if (
    top + height > viewBottom - edge &&
    rect.top - gap - height >= viewTop + edge
  ) {
    top = rect.top - gap - height;
    flipped = true;
  }
  top = Math.max(Math.min(top, viewBottom - height - edge), viewTop + edge);
  let left =
    options?.placement === "bottom-end" ? rect.right - width : rect.left;
  left = Math.min(Math.max(left, edge), viewportWidth - width - edge);

  return {
    top: Math.round(top),
    left: Math.round(Math.max(left, edge)),
    minWidth: Math.round(Math.max(rect.width, options?.minWidth ?? 0)),
    flipped,
  };
}
